import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from "react";
import { useGetRole } from "../../api/hooks/usePermission";
import { showError } from "../../helpers/toast";
import type { Role } from "../../api/types/permission.types";

const RoleDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const roleId = location?.state?.roleId;

  // Fetch role data
  const { data: roleData, isLoading: isLoadingRole, error: roleError } = useGetRole(
    roleId || '',
    { enabled: !!roleId }
  );

  const role: Role | null = roleData?.data || null;

  useEffect(() => {
    if (roleError) {
      showError((roleError as any)?.response?.data?.message || "Failed to load role details");
    }
  }, [roleError]);

  useEffect(() => {
    if (!roleId && !isLoadingRole) {
      showError("No role selected. Redirecting to roles list.");
      navigate("/manage-subadmin/roles");
    }
  }, [roleId, isLoadingRole, navigate]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  if (isLoadingRole) {
    return (
      <>
        <div className="main_title">
          <h1>Role Detail</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button>
            {" "} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
            {" "} - <button onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</button>
            {" "} - Role Detail
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!role) {
    return (
      <>
        <div className="main_title">
          <h1>Role Detail</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button>
            {" "} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
            {" "} - <button onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</button>
            {" "} - Role Detail
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <p>Role not found.</p>
        </Box>
      </>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>Role Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</button>
          {" "} - Role Detail
        </p>
      </div>

      <div className="cards view_page">
        <div className="table_header">
          <h2>Role Details</h2>
          <div className="right_s">
            <Button
              className="btn btn_primary sm"
              onClick={() => navigate("/manage-subadmin/roles/add", { state: { roleId: role._id } })}
            >
              Edit Role
            </Button>
          </div>
        </div>

        <div className="gap_p">
          <div className="w_33">
            <h6>Role Name</h6>
            <p>{role.name || "N/A"}</p>
          </div>
          <div className="w_33">
            <h6>Created Date</h6>
            <p>{formatDate(role.createdAt)}</p>
          </div>
        </div>

        <TableContainer className="table_container mt_20">
          <Table>
            <TableHead>
              <TableRow hover>
                <TableCell>Module Name</TableCell>
                <TableCell align="center">View</TableCell>
                <TableCell align="center">Delete</TableCell>
                <TableCell align="center">Add/Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {role.permission && role.permission.length > 0 ? (
                role.permission.map((perm, index) => (
                  <TableRow hover key={perm.sideBarId || index}>
                    <TableCell>{perm.label || "N/A"}</TableCell>
                    <TableCell align="center">
                      {perm.isView ? (
                        <DoneIcon className="c_success" />
                      ) : (
                        <CloseIcon className="c_danger" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {perm.isDelete ? (
                        <DoneIcon className="c_success" />
                      ) : (
                        <CloseIcon className="c_danger" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {perm.isAdd && perm.isEdit ? (
                        <DoneIcon className="c_success" />
                      ) : (
                        <CloseIcon className="c_danger" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No permissions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default RoleDetail;
